/**
 * Test Mode tab copy (TM-8).
 *
 * ⚠️ ALL strings are PLACEHOLDER until product finalizes copy. Per the work
 * plan, every user-facing string for this feature lives here as a constant,
 * never inline in components, so the final copy pass is a single-file change.
 *
 * Wording rule (guiding principle): exit is always a REQUEST the user
 * confirms on their device. Action copy must say "Request to exit",
 * never "Remove".
 */
export const TEST_MODE_COPY = {
    pageTitle: 'POM - Test Mode',
    heading: 'Test Mode',
    description:
        'Monitor onboarding and test-mode progress: who is in test mode, who has tested, and who needs follow-up.',

    kpi: {
        inTestMode: { label: 'In test mode', caption: 'Currently active' },
        completed: { label: 'Tests completed', caption: 'App test done' },
        notTested: { label: 'Not yet tested', caption: 'In test mode, no test yet' },
        flagged: { label: 'Flagged', caption: 'Exited untested / untested device' },
        filteringChip: 'FILTERING ✕',
        flaggedUnavailable: 'Flag counts arrive once the backend exposes flag data.',
    },

    table: {
        headers: {
            user: 'User',
            groupTeam: 'Group / Team',
            status: 'Status',
            window: 'In test mode / expires',
            appTest: 'App test',
            device: 'Device',
            flag: 'Flag',
            actions: 'Actions',
        },
        status: {
            inTestMode: 'In test mode',
            live: 'Live',
            flagged: 'Flagged',
        },
        appTest: { tested: 'Tested', notTested: 'Not tested' },
        device: {
            none: 'None',
            pairedUntested: 'Untested',
            pairedTested: 'Tested',
        },
        flags: {
            exitedWithoutTest: 'Exited test mode without a completed test',
            pairedDeviceUntested: 'Paired device has not been tested',
        },
        window: {
            entered: (relative: string) => `Entered ${relative}`,
            expires: (relative: string) => `Expires ${relative}`,
            lastSession: (relative: string) => `Last session ${relative}`,
            never: 'Never in test mode',
        },
        requestExit: 'Request to exit',
        exitRequested: 'Exit requested',
    },

    selection: {
        bulkRequestExit: (n: number) => `Request to exit test mode (${n})`,
        bulkRequestExitEmpty: 'Request to exit test mode',
        bulkEnroll: (n: number) => `Put into test mode (${n})`,
        bulkEnrollEmpty: 'Put into test mode',
        selectedCount: (n: number) => `${n} selected`,
        clearSelection: 'Clear selection',
        hint: 'Select users to act on a cohort at once',
        userCount: (shown: number, total: number) => `${shown} of ${total} users`,
    },

    actions: {
        requestExitTitle: 'Request exit from Test Mode?',
        requestExitBody: (name: string) =>
            `${name} will receive a notification asking them to confirm going live. Nothing changes until they confirm on their own device.`,
        bulkRequestExitTitle: (n: number) => `Request ${n} ${n === 1 ? 'user' : 'users'} to exit Test Mode?`,
        bulkRequestExitBody:
            'Each selected user receives a notification asking them to confirm going live. No one is removed from test mode until they confirm on their own device.',
        bulkEnrollTitle: (n: number) => `Put ${n} ${n === 1 ? 'user' : 'users'} into Test Mode?`,
        bulkEnrollBody:
            'Selected users are enrolled immediately and notified by push and SMS. Their SOS routes to the training line until they exit or their window expires.',
        toastExitRequested: (n: number) =>
            n === 1 ? 'Exit request sent.' : `Exit requests sent to ${n} users.`,
        toastEnrolled: (n: number) =>
            n === 1 ? '1 user put into test mode.' : `${n} users put into test mode.`,
        toastPartial: (ok: number, failed: number) =>
            `${ok} succeeded, ${failed} failed. Retry the failed users or check their status.`,
        toastAllFailed: 'The action failed for every selected user. Check your connection and try again.',
    },

    filters: {
        status: {
            label: 'Status',
            options: [
                { label: 'All', value: '' },
                { label: 'In test mode', value: 'in-test' },
                { label: 'Live', value: 'live' },
                { label: 'Flagged', value: 'flagged' },
            ],
        },
        appTest: {
            label: 'App Test',
            options: [
                { label: 'All', value: '' },
                { label: 'Tested', value: 'yes' },
                { label: 'Not tested', value: 'no' },
            ],
        },
        device: {
            label: 'Device',
            options: [
                { label: 'All', value: '' },
                { label: 'None', value: 'none' },
                { label: 'Paired · untested', value: 'paired-untested' },
                { label: 'Paired · tested', value: 'paired-tested' },
            ],
        },
        name: { label: 'User Name', placeholder: 'Search by name...' },
        group: { label: 'Group' },
        team: { label: 'Team' },
    },

    csvFileName: 'test-mode-roster',

    report: {
        title: 'Onboarding progress report',
        description:
            'How did onboarding go? A broader completion view across your scope, for leadership follow-up with managers.',
        headers: {
            user: 'User',
            groupTeam: 'Group / Team',
            registered: 'Registered',
            paired: 'Paired device',
            testedApp: 'Tested app',
            testedDevice: 'Tested device',
            progress: 'Progress',
        },
        rangeOptions: [
            { label: 'Last 7 days', value: '7d' },
            { label: 'Last 30 days', value: '30d' },
            { label: 'Last 90 days', value: '90d' },
        ],
        allGroups: 'All groups',
        allTeams: 'All teams',
        exportCsv: 'Export CSV',
        csvFileName: 'onboarding-progress',
        progressLabel: (done: number, of: number) => `${done}/${of}`,
        empty: 'No onboarding activity in this range yet.',
    },

    errors: {
        roster: 'Could not load the test-status roster. It retries automatically; check your connection or try refreshing.',
        report: 'Could not load the onboarding-progress report. Check your connection or try refreshing.',
        exportCsv: 'Failed to export the CSV. Please try again.',
    },

    allClear: {
        title: 'All caught up, nothing to nudge',
        body: 'Everyone in your scope has completed testing. The roster below is live.',
    },

    empty: {
        noMatchTitle: 'No users match these filters',
        noMatchBody: 'Try widening the group, team, or status filters.',
        noMatchButton: 'Clear filters',
        noneTitle: 'No test-mode activity yet',
        noneBody:
            'Once users start onboarding in test mode, their status shows up here. Data for this list arrives when the reporting pipeline is deployed to this environment.',
    },
} as const;
